import React, { useCallback, useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../../supabase/supabase';
import {
  createCustomerServiceAccount,
  fetchCustomerServiceUsers,
  setCustomerServiceRole
} from '../../services/customerServiceService';

const {
  FiCheckCircle,
  FiCopy,
  FiExternalLink,
  FiLoader,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiTrash2,
  FiX
} = FiIcons;

const CUSTOMER_SERVICE_PORTAL_PATH = '/customer-service-dashboard';

const AdminCustomerServiceManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [temporaryCredentials, setTemporaryCredentials] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deletePin, setDeletePin] = useState('');
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUsers((await fetchCustomerServiceUsers() || []).filter((person) => person.role === 'customer_service'));
    } catch (requestError) {
      setError(requestError.message || 'Unable to load user roles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const searchAccounts = async (event) => {
    event.preventDefault();
    const query = searchQuery.trim().toLowerCase();
    if (query.length < 2) return;

    setSearching(true);
    setError('');
    setSearchResults([]);
    try {
      const { data, error: searchError } = await supabase.rpc('admin_get_user_list');
      if (searchError) throw searchError;
      setSearchResults((data || [])
        .filter((person) => {
          const matchesQuery = `${person.name || ''} ${person.email || ''}`.toLowerCase().includes(query);
          return person.role === 'buyer' && matchesQuery;
        })
        .slice(0, 30));
    } catch (requestError) {
      setError(requestError.message || 'Unable to search user accounts.');
    } finally {
      setSearching(false);
    }
  };

  const createAccount = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');
    setTemporaryCredentials(null);

    try {
      const result = await createCustomerServiceAccount({ name, email });
      setName('');
      setEmail('');
      setInviteOpen(false);
      setTemporaryCredentials({
        email: result.email || email.trim(),
        password: result.temporary_password
      });
      setSuccess('Staff account created and confirmed. Share the temporary password manually.');
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to create the staff account.');
    } finally {
      setCreating(false);
    }
  };

  const toggleRole = async (person) => {
    const enabled = person.role !== 'customer_service';
    if (!window.confirm(`${enabled ? 'Assign' : 'Remove'} customer-service access for ${person.name}?`)) return;
    setBusyId(person.id);
    setError('');
    setSuccess('');
    setTemporaryCredentials(null);
    try {
      await setCustomerServiceRole(person.id, enabled);
      if (enabled) {
        setSearchResults((current) => current.filter((user) => user.id !== person.id));
      }
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to update the role.');
    } finally {
      setBusyId('');
    }
  };

  const regeneratePassword = async (person) => {
    if (!window.confirm(`Generate a new temporary password for ${person.name}? Their current password will stop working.`)) return;

    setBusyId(person.id);
    setError('');
    setSuccess('');
    setTemporaryCredentials(null);
    try {
      const { data, error: functionError } = await supabase.functions.invoke(
        'admin-create-customer-service-account',
        { body: { action: 'regenerate_password', user_id: person.id } }
      );

      if (functionError) {
        let message = functionError.message;
        try {
          const response = await functionError.context?.json?.();
          message = response?.error || message;
        } catch {
          // Use the Functions client message when the response has no JSON body.
        }
        throw new Error(message || 'Unable to regenerate the temporary password.');
      }

      if (data?.error) throw new Error(data.error);

      setTemporaryCredentials({
        email: data.email || person.email,
        password: data.temporary_password
      });
      setSuccess(`A new temporary password was generated for ${person.name}. Share it manually.`);
    } catch (requestError) {
      setError(requestError.message || 'Unable to regenerate the temporary password.');
    } finally {
      setBusyId('');
    }
  };

  const openDeleteConfirmation = (person) => {
    setDeleteTarget(person);
    setDeleteEmail('');
    setDeletePin('');
    setError('');
    setSuccess('');
  };

  const closeDeleteConfirmation = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteEmail('');
    setDeletePin('');
  };

  const deleteStaffAccount = async (event) => {
    event.preventDefault();
    if (!deleteTarget) return;

    setDeleting(true);
    setError('');
    setSuccess('');
    setTemporaryCredentials(null);
    try {
      const { data, error: functionError } = await supabase.functions.invoke(
        'admin-delete-user',
        {
          body: {
            user_id: deleteTarget.id,
            confirmation_email: deleteEmail.trim(),
            pin: deletePin
          }
        }
      );

      if (functionError) {
        let message = functionError.message;
        try {
          const response = await functionError.context?.json?.();
          message = response?.error || message;
        } catch {
          // Use the Functions client message when the response has no JSON body.
        }
        throw new Error(message || 'Unable to delete the customer-service account.');
      }

      if (data?.error) throw new Error(data.error);
      setSuccess(`Customer-service account for ${deleteTarget.name} was permanently deleted.`);
      setDeleteTarget(null);
      setDeleteEmail('');
      setDeletePin('');
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to delete the customer-service account.');
    } finally {
      setDeleting(false);
    }
  };

  const copyTemporaryPassword = async () => {
    if (!temporaryCredentials?.password) return;

    try {
      await navigator.clipboard.writeText(temporaryCredentials.password);
      setSuccess('Temporary password copied. Share it securely with the staff member.');
    } catch {
      setError('Unable to copy the password automatically. Select and copy it manually.');
    }
  };

  const copyPortalLink = async () => {
    const portalUrl = `${window.location.origin}${CUSTOMER_SERVICE_PORTAL_PATH}`;
    setError('');
    setSuccess('');

    try {
      await navigator.clipboard.writeText(portalUrl);
      setSuccess('Customer-service dashboard link copied.');
    } catch {
      setError('Unable to copy the link automatically. Open the dashboard link and copy its address from your browser.');
    }
  };

  return (
    <section className="mt-8 rounded-[2.5rem] border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-gray-900">Customer Service Access</h2>
          <p className="mt-1 text-xs text-slate-500">Invite new staff or grant access to an existing buyer account.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => { setInviteOpen(true); setError(''); }} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-[9px] font-black uppercase tracking-widest text-white">
            <SafeIcon icon={FiPlus} /> Add staff
          </button>
          <button type="button" onClick={refresh} className="flex items-center gap-2 rounded-xl bg-indigo-50 px-4 py-3 text-[9px] font-black uppercase tracking-widest text-indigo-600">
            <SafeIcon icon={FiRefreshCw} /> Refresh users
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-indigo-700">Staff dashboard link</p>
          <p className="mt-1 break-all text-sm font-medium text-slate-600">
            {window.location.origin}{CUSTOMER_SERVICE_PORTAL_PATH}
          </p>
          <p className="mt-1 text-xs text-slate-500">Staff must sign in with an account assigned customer-service access.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={copyPortalLink}
            className="flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-[9px] font-black uppercase tracking-widest text-indigo-600 shadow-sm"
          >
            <SafeIcon icon={FiCopy} /> Copy link
          </button>
          <a
            href={CUSTOMER_SERVICE_PORTAL_PATH}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-[9px] font-black uppercase tracking-widest text-white"
          >
            <SafeIcon icon={FiExternalLink} /> Open dashboard
          </a>
        </div>
      </div>

      {error && <p className="mt-4 rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-700">{error}</p>}
      {success && <p className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm font-bold text-emerald-700">{success}</p>}

      {temporaryCredentials && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-amber-800">Temporary password — share manually</p>
              <p className="mt-1 text-sm text-amber-900">{temporaryCredentials.email}</p>
              <code className="mt-2 block select-all break-all rounded-lg bg-white px-3 py-2 font-mono text-sm text-slate-900">
                {temporaryCredentials.password}
              </code>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyTemporaryPassword}
                className="flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-[9px] font-black uppercase tracking-widest text-amber-800 shadow-sm"
              >
                <SafeIcon icon={FiCopy} /> Copy password
              </button>
              <button
                type="button"
                onClick={() => setTemporaryCredentials(null)}
                aria-label="Hide temporary password"
                className="rounded-xl bg-white p-2 text-slate-500 shadow-sm"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-amber-800">This password is shown only in this session. It is not saved in the app.</p>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-slate-100 p-4">
        <h3 className="font-black text-slate-800">Find an existing user</h3>
        <p className="mt-1 text-xs text-slate-500">Search buyers by name or email to grant customer-service access.</p>
        <form onSubmit={searchAccounts} className="mt-3 flex flex-wrap gap-2">
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            minLength={2}
            placeholder="Search name or email"
            aria-label="Search existing users by name or email"
            className="min-w-[220px] flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-indigo-400"
          />
          <button
            type="submit"
            disabled={searching || searchQuery.trim().length < 2}
            className="flex items-center gap-2 rounded-xl bg-indigo-50 px-4 py-3 text-[9px] font-black uppercase tracking-widest text-indigo-600 disabled:opacity-50"
          >
            <SafeIcon icon={searching ? FiLoader : FiSearch} className={searching ? 'animate-spin' : ''} />
            Search users
          </button>
        </form>
        {searchResults.length > 0 && (
          <div className="mt-3 divide-y divide-slate-100">
            {searchResults.map((person) => (
              <div key={person.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div>
                  <p className="font-bold text-slate-800">{person.name}</p>
                  <p className="text-xs text-slate-500">{person.email}</p>
                </div>
                <button
                  type="button"
                  disabled={busyId === person.id}
                  onClick={() => toggleRole(person)}
                  className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-[9px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                >
                  {busyId === person.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiShield} />}
                  Make rep
                </button>
              </div>
            ))}
          </div>
        )}
        {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
          <p className="mt-3 text-sm text-slate-500">No matching buyer accounts found.</p>
        )}
      </div>

      {loading ? <SafeIcon icon={FiLoader} className="mt-5 animate-spin text-2xl text-indigo-500" /> : (
        <div className="mt-5 divide-y divide-slate-100">
          {users.map((person) => (
            <div key={person.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div>
                <p className="font-bold text-slate-800">{person.name}</p>
                <p className="text-xs text-slate-500">{person.email} · {person.role}</p>
                {person.role === 'customer_service' && (
                  <p className="mt-1 text-xs text-slate-500">
                    {person.is_confirmed ? 'Account confirmed' : 'Invitation not yet accepted'}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {person.role === 'customer_service' && (
                  <button
                    type="button"
                    disabled={busyId === person.id}
                    onClick={() => regeneratePassword(person)}
                    className="flex items-center gap-2 rounded-xl bg-indigo-50 px-4 py-2 text-[9px] font-black uppercase tracking-widest text-indigo-600 disabled:opacity-50"
                  >
                    {busyId === person.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiRefreshCw} />}
                    Regenerate password
                  </button>
                )}
                {['buyer', 'customer_service'].includes(person.role) && (
                  <button type="button" disabled={busyId === person.id} onClick={() => toggleRole(person)} className={`flex items-center gap-2 rounded-xl px-4 py-2 text-[9px] font-black uppercase tracking-widest disabled:opacity-50 ${person.role === 'customer_service' ? 'bg-rose-50 text-rose-600' : 'bg-indigo-600 text-white'}`}>
                    {busyId === person.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : person.role === 'customer_service' ? <SafeIcon icon={FiCheckCircle} /> : <SafeIcon icon={FiShield} />}
                    {person.role === 'customer_service' ? 'Remove access' : 'Make rep'}
                  </button>
                )}
                {person.role === 'customer_service' && (
                  <button
                    type="button"
                    disabled={busyId === person.id}
                    onClick={() => openDeleteConfirmation(person)}
                    className="flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-[9px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                  >
                    <SafeIcon icon={FiTrash2} /> Delete account
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !creating) setInviteOpen(false);
        }}>
          <div role="dialog" aria-modal="true" aria-labelledby="customer-service-invite-title" className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 id="customer-service-invite-title" className="text-xl font-black text-gray-900">Add customer-service staff</h3>
                <p className="mt-1 text-sm text-slate-500">The account will be confirmed without an email. A temporary password will be shown here for you to share manually.</p>
              </div>
              <button type="button" disabled={creating} onClick={() => setInviteOpen(false)} aria-label="Close invitation dialog" className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50">
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <form onSubmit={createAccount} className="mt-5 grid gap-3">
              <input
                required
                minLength={2}
                maxLength={120}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Staff name"
                aria-label="Staff name"
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-indigo-400"
              />
              <input
                required
                type="email"
                maxLength={254}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Staff email"
                aria-label="Staff email"
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-indigo-400"
              />
              {error && <p className="rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-700">{error}</p>}
              <div className="mt-2 flex justify-end gap-2">
                <button type="button" disabled={creating} onClick={() => setInviteOpen(false)} className="rounded-xl bg-slate-100 px-4 py-3 text-[9px] font-black uppercase tracking-widest text-slate-600 disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-[9px] font-black uppercase tracking-widest text-white disabled:opacity-50">
                  <SafeIcon icon={creating ? FiLoader : FiPlus} className={creating ? 'animate-spin' : ''} />
                  {creating ? 'Creating account...' : 'Create account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeDeleteConfirmation();
        }}>
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="customer-service-delete-title"
            onSubmit={deleteStaffAccount}
            className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 id="customer-service-delete-title" className="text-xl font-black text-rose-700">Permanently delete staff account?</h3>
                <p className="mt-2 text-sm text-slate-600">
                  This permanently removes <strong>{deleteTarget.name}</strong> ({deleteTarget.email}) from Supabase Auth and LocalMarket. They will no longer be able to sign in. This action cannot be undone.
                </p>
              </div>
              <button type="button" disabled={deleting} onClick={closeDeleteConfirmation} aria-label="Close deletion confirmation" className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50">
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <div className="mt-5 grid gap-3">
              <label className="grid gap-1 text-xs font-bold text-slate-700">
                Enter this staff member’s email address
                <input
                  required
                  type="email"
                  value={deleteEmail}
                  onChange={(event) => setDeleteEmail(event.target.value)}
                  autoComplete="off"
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-normal outline-none focus:border-rose-400"
                />
              </label>
              <label className="grid gap-1 text-xs font-bold text-slate-700">
                Admin PIN
                <input
                  required
                  type="password"
                  inputMode="numeric"
                  minLength={8}
                  maxLength={12}
                  pattern="[0-9]{8,12}"
                  value={deletePin}
                  onChange={(event) => setDeletePin(event.target.value)}
                  autoComplete="off"
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-normal outline-none focus:border-rose-400"
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" disabled={deleting} onClick={closeDeleteConfirmation} className="rounded-xl bg-slate-100 px-4 py-3 text-[9px] font-black uppercase tracking-widest text-slate-600 disabled:opacity-50">
                Cancel
              </button>
              <button
                type="submit"
                disabled={deleting}
                className="flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-3 text-[9px] font-black uppercase tracking-widest text-white disabled:opacity-50"
              >
                {deleting && <SafeIcon icon={FiLoader} className="animate-spin" />}
                {deleting ? 'Deleting account...' : 'Permanently delete'}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};

export default AdminCustomerServiceManagementPanel;
